/**
 * Main Application Router
 * Configures all routes with proper protection and lazy loading
 */

import React, { Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import ProtectedRoute, { PublicRoute } from '../components/auth/ProtectedRoute';
import { usePermissions } from '../hooks/usePermissions';
import NavigationLayout from '../components/navigation/NavigationLayout';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorBoundary from '../components/common/ErrorBoundary';

import { robustLazy as lazy } from '../utils/lazyRetry';

// Lazy load pages
const LoginPage = lazy(() => import('../pages/auth/LoginPage'));
const RegisterPage = lazy(() => import('../pages/auth/RegisterPage'));
const ForgotPasswordPage = lazy(
  () => import('../pages/auth/ForgotPasswordPage')
);
const DashboardPage = lazy(() => import('../pages/Dashboard/DashboardPage'));
const VendorsPage = lazy(() => import('../pages/Vendors/VendorsPage'));
const VendorDetailPage = lazy(() => import('../pages/Vendor/VendorDetailPage'));
const VendorFormPage = lazy(() => import('../pages/Vendor/VendorFormPage'));
const CustomersPage = lazy(() => import('../pages/Customers/CustomersPage'));
const PurchasesPage = lazy(() => import('../pages/purchases/PurchasesPage'));
const PurchaseDetailPage = lazy(() => import('../pages/purchases/PurchaseDetail'));
const PurchaseFormPage = lazy(() => import('../pages/purchases/PurchaseForm'));
const SalesPage = lazy(() => import('../pages/Sales/SalesPage'));
const SalesFormPage = lazy(() => import('../pages/Sales/SalesFormPage'));
const SalesDetailPage = lazy(() => import('../pages/Sales/SalesDetailPage'));
const RetailSalesFormPage = lazy(() => import('../pages/Sales/RetailSalesFormPage'));
const RetailSalesDetailPage = lazy(() => import('../pages/Sales/RetailSalesDetailPage'));
const DailySalesPage = lazy(() => import('../pages/Sales/DailySalesPage'));
const CustomerFormPage = lazy(() => import('../pages/Customer/CustomerFormPage'));
const CustomerDetailPage = lazy(() => import('../pages/Customer/CustomerDetailPage'));
const ExpensesPage = lazy(() => import('../pages/Expenses/ExpensesPage'));
const ExpenseFormPage = lazy(() => import('../pages/Expenses/ExpenseForm'));
const ExpenseCategoriesPage = lazy(() => import('../pages/Expenses/ExpenseCategoriesPage'));
const IncomeExpensePage = lazy(() => import('../pages/Finance/IncomeExpensePage'));
const AddIncomePage = lazy(() => import('../pages/Finance/AddIncomePage'));
const VehiclesPage = lazy(() => import('../pages/Vehicles/VehiclesPage'));
const ReportsPage = lazy(() => import('../pages/Reports/ReportsPage'));
const SettingsPage = lazy(() => import('../pages/settings/SettingsPage'));
const Profile = lazy(() => import('../pages/Profile'));
const UserListPage = lazy(() => import('../pages/User/UserListPage'));
const UserFormPage = lazy(() => import('../pages/User/UserFormPage'));
const UserDetailPage = lazy(() => import('../pages/User/UserDetailPage'));
const AuditLogPage = lazy(() => import('../pages/AuditLog/AuditLogPage'));
const TestAuthPage = lazy(() => import('../pages/TestAuth'));
const NotFoundPage = lazy(() => import('../pages/errors/NotFoundPage'));
const ServerErrorPage = lazy(() => import('../pages/errors/ServerErrorPage'));
const LandingPage = lazy(() => import('../pages/Landing/LandingPage'));

// Super Admin Pages
const SuperAdminDashboard = lazy(() => import('../pages/SuperAdmin/SuperAdminDashboard'));
const SuperAdminCompanies = lazy(() => import('../pages/SuperAdmin/SuperAdminCompanies'));
const SuperAdminUsers = lazy(() => import('../pages/SuperAdmin/SuperAdminUsers'));
const GlobalFinancialDashboard = lazy(() => import('../pages/SuperAdmin/Financials/GlobalFinancialDashboard'));

/**
 * Loading fallback component for lazy-loaded routes
 */
const RouteLoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center">
    <LoadingSpinner size="large" />
  </div>
);

/**
 * Wrapper for lazy-loaded components
 */
const LazyWrapper = ({ children }) => (
  <Suspense fallback={<LoadingSpinner />}>{children}</Suspense>
);

/**
 * AppRouter component
 * Main router configuration with all application routes
 */
const AppRouter = () => {
  const { can } = usePermissions();
  return (
    <ErrorBoundary>
      <Suspense fallback={<RouteLoadingFallback />}>
        <Routes>
          {/* Public landing page as default */}
          <Route
            path="/"
            element={
              <LazyWrapper>
                <LandingPage />
              </LazyWrapper>
            }
          />

          {/* Public routes */}
          <Route
            path="/login"
            element={
              <PublicRoute>
                <LazyWrapper>
                  <LoginPage />
                </LazyWrapper>
              </PublicRoute>
            }
          />
          <Route
            path="/register"
            element={
              <PublicRoute>
                <LazyWrapper>
                  <RegisterPage />
                </LazyWrapper>
              </PublicRoute>
            }
          />
          <Route
            path="/test-auth"
            element={
              <LazyWrapper>
                <TestAuthPage />
              </LazyWrapper>
            }
          />
          <Route
            path="/auth/forgot-password"
            element={
              <PublicRoute>
                <LazyWrapper>
                  <ForgotPasswordPage />
                </LazyWrapper>
              </PublicRoute>
            }
          />

          {/* Legacy redirects */}
          <Route path="/customers" element={<Navigate to="/buyers" replace />} />
          <Route path="/customers/*" element={<Navigate to="/buyers" replace />} />

          {/* Protected routes with layout */}
          <Route
            path="/*"
            element={
              <ProtectedRoute>
                <NavigationLayout>
                  <Routes>
                    <Route
                      path="finance/income-expense"
                      element={
                        <LazyWrapper>
                          <ProtectedRoute requiredPermissions={['reports:view']}>
                            <IncomeExpensePage />
                          </ProtectedRoute>
                        </LazyWrapper>
                      }
                    />
                    <Route
                      path="finance/income/new"
                      element={
                        <LazyWrapper>
                          <ProtectedRoute requiredPermissions={['sales:view']}>
                            <AddIncomePage />
                          </ProtectedRoute>
                        </LazyWrapper>
                      }
                    />
                    <Route
                      path="dashboard"
                      element={
                        <LazyWrapper>
                          <DashboardPage />
                        </LazyWrapper>
                      }
                    />
                    <Route
                      path="vendors"
                      element={
                        <LazyWrapper>
                          <ProtectedRoute requiredPermissions={['vendors:view']}>
                             <VendorsPage />
                          </ProtectedRoute>
                        </LazyWrapper>
                      }
                    />
                    <Route
                      path="vendors/new"
                      element={
                        <LazyWrapper>
                          <VendorFormPage />
                        </LazyWrapper>
                      }
                    />
                    <Route
                      path="vendors/:id/edit"
                      element={
                        <LazyWrapper>
                          <VendorFormPage />
                        </LazyWrapper>
                      }
                    />
                    <Route
                      path="vendors/:id"
                      element={
                        <LazyWrapper>
                          <VendorDetailPage />
                        </LazyWrapper>
                      }
                    />
                    <Route
                      path="buyers"
                      element={
                        <LazyWrapper>
                          <ProtectedRoute requiredPermissions={['customers:view']}>
                            <CustomersPage />
                          </ProtectedRoute>
                        </LazyWrapper>
                      }
                    />
                    <Route
                      path="buyers/new"
                      element={
                        <LazyWrapper>
                          <CustomerFormPage />
                        </LazyWrapper>
                      }
                    />
                    <Route
                      path="buyers/:id/edit"
                      element={
                        <LazyWrapper>
                          <CustomerFormPage />
                        </LazyWrapper>
                      }
                    />
                    <Route
                      path="buyers/:id"
                      element={
                        <LazyWrapper>
                          <CustomerDetailPage />
                        </LazyWrapper>
                      }
                    />
                    <Route
                      path="purchases"
                      element={
                        <LazyWrapper>
                          <ProtectedRoute requiredPermissions={['purchases:view']}>
                            <PurchasesPage />
                          </ProtectedRoute>
                        </LazyWrapper>
                      }
                    />
                    <Route
                      path="purchases/new"
                      element={
                        <LazyWrapper>
                          <PurchaseFormPage />
                        </LazyWrapper>
                      }
                    />
                    <Route
                      path="purchases/:id/edit"
                      element={
                        <LazyWrapper>
                          <PurchaseFormPage />
                        </LazyWrapper>
                      }
                    />
                    <Route
                      path="purchases/:id"
                      element={
                        <LazyWrapper>
                          <PurchaseDetailPage />
                        </LazyWrapper>
                      }
                    />
                    <Route
                      path="sales"
                      element={
                        <LazyWrapper>
                          <ProtectedRoute requiredPermissions={['sales:view']}>
                            <SalesPage />
                          </ProtectedRoute>
                        </LazyWrapper>
                      }
                    />
                    <Route
                      path="sales/new"
                      element={
                        <LazyWrapper>
                          <SalesFormPage />
                        </LazyWrapper>
                      }
                    />
                    <Route
                      path="sales/new-retail"
                      element={
                        <LazyWrapper>
                          <RetailSalesFormPage />
                        </LazyWrapper>
                      }
                    />
                    <Route
                      path="sales/daily"
                      element={
                        <LazyWrapper>
                          <DailySalesPage />
                        </LazyWrapper>
                      }
                    />
                    <Route
                      path="sales/:id/edit"
                      element={
                        <LazyWrapper>
                          <SalesFormPage />
                        </LazyWrapper>
                      }
                    />
                    <Route
                      path="sales/retail/:id/edit"
                      element={
                        <LazyWrapper>
                          <RetailSalesFormPage />
                        </LazyWrapper>
                      }
                    />
                    <Route
                      path="sales/:id"
                      element={
                        <LazyWrapper>
                          <SalesDetailPage />
                        </LazyWrapper>
                      }
                    />
                    <Route
                      path="sales/retail/:id"
                      element={
                        <LazyWrapper>
                          <RetailSalesDetailPage />
                        </LazyWrapper>
                      }
                    />
                    <Route
                      path="expenses"
                      element={
                        <LazyWrapper>
                          <ProtectedRoute requiredPermissions={['expenses:view']}>
                            <ExpensesPage />
                          </ProtectedRoute>
                        </LazyWrapper>
                      }
                    />
                    <Route
                      path="expenses/new"
                      element={
                        <LazyWrapper>
                          <ExpenseFormPage />
                        </LazyWrapper>
                      }
                    />
                    <Route
                      path="expenses/categories"
                      element={
                        <LazyWrapper>
                          <ExpenseCategoriesPage />
                        </LazyWrapper>
                      }
                    />
                    <Route
                      path="vehicles"
                      element={
                        <LazyWrapper>
                          <ProtectedRoute>
                            <VehiclesPage />
                          </ProtectedRoute>
                        </LazyWrapper>
                      }
                    />
                    <Route
                      path="reports"
                      element={
                        <LazyWrapper>
                          <ProtectedRoute>
                            <ReportsPage />
                          </ProtectedRoute>
                        </LazyWrapper>
                      }
                    />
                    <Route
                      path="settings"
                      element={
                        <LazyWrapper>
                          <SettingsPage />
                        </LazyWrapper>
                      }
                    />
                    <Route
                      path="profile"
                      element={
                        <LazyWrapper>
                          <Profile />
                        </LazyWrapper>
                      }
                    />
                    <Route
                      path="users"
                      element={
                        <LazyWrapper>
                          <UserListPage />
                        </LazyWrapper>
                      }
                    />
                    <Route
                      path="users/new"
                      element={
                        <LazyWrapper>
                          <UserFormPage />
                        </LazyWrapper>
                      }
                    />
                    <Route
                      path="users/:id/edit"
                      element={
                        <LazyWrapper>
                          <UserFormPage />
                        </LazyWrapper>
                      }
                    />
                    <Route
                      path="users/:id"
                      element={
                        <LazyWrapper>
                          <UserDetailPage />
                        </LazyWrapper>
                      }
                    />
                    <Route
                      path="audit-logs"
                      element={
                        <LazyWrapper>
                          <AuditLogPage />
                        </LazyWrapper>
                      }
                    />

                    {/* SUPER ADMIN ROUTES */}
                    <Route
                      path="superadmin/dashboard"
                      element={
                        <LazyWrapper>
                          <ProtectedRoute requiredRole="SUPER_ADMIN">
                            <SuperAdminDashboard />
                          </ProtectedRoute>
                        </LazyWrapper>
                      }
                    />
                    <Route
                      path="superadmin/companies"
                      element={
                        <LazyWrapper>
                          <ProtectedRoute requiredRole="SUPER_ADMIN">
                            <SuperAdminCompanies />
                          </ProtectedRoute>
                        </LazyWrapper>
                      }
                    />
                    <Route
                      path="superadmin/users"
                      element={
                        <LazyWrapper>
                          <ProtectedRoute requiredRole="SUPER_ADMIN">
                            <SuperAdminUsers />
                          </ProtectedRoute>
                        </LazyWrapper>
                      }
                    />
                    <Route
                      path="superadmin/financials/*"
                      element={
                        <LazyWrapper>
                          <ProtectedRoute requiredRole="SUPER_ADMIN">
                            <GlobalFinancialDashboard />
                          </ProtectedRoute>
                        </LazyWrapper>
                      }
                    />
                  </Routes>
                </NavigationLayout>
              </ProtectedRoute>
            }
          />

          {/* Error routes */}
          <Route
            path="/error"
            element={
              <LazyWrapper>
                <ServerErrorPage />
              </LazyWrapper>
            }
          />
          <Route
            path="*"
            element={
              <LazyWrapper>
                <NotFoundPage />
              </LazyWrapper>
            }
          />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
};

export default AppRouter;
